<script lang="ts">
  import { onMount } from "svelte";
  import Editor from "./routes/Editor.svelte";
  import SessionGate from "./routes/SessionGate.svelte";
  import { api, ApiError, type PasteResponse } from "./lib/api";

  type Route = { kind: "landing" } | { kind: "new" } | { kind: "paste"; slug: string };

  function parse(): Route {
    const m = location.pathname.match(/^\/p\/([A-Za-z0-9]+)\/?$/);
    if (m) return { kind: "paste", slug: m[1]! };
    if (location.pathname === "/new") return { kind: "new" };
    return { kind: "landing" };
  }

  let route = $state<Route>(parse());
  let paste = $state<PasteResponse | null>(null);
  let loading = $state(false);
  let notFound = $state(false);
  let hasSession = $state(false);
  let checkedSession = $state(false);

  async function checkSession() {
    try {
      await api.me();
      hasSession = true;
    } catch (e) {
      hasSession = false;
      if (!(e instanceof ApiError && e.status === 401)) console.error(e);
    } finally {
      checkedSession = true;
    }
  }

  async function loadPaste(slug: string) {
    loading = true;
    notFound = false;
    try {
      paste = await api.getPaste(slug);
    } catch {
      notFound = true;
      paste = null;
    } finally {
      loading = false;
    }
  }

  function applyRoute() {
    route = parse();
    paste = null;
    notFound = false;
    if (route.kind === "paste") void loadPaste(route.slug);
  }

  onMount(() => {
    const onPop = () => applyRoute();
    window.addEventListener("popstate", onPop);
    void checkSession().then(() => applyRoute());
    return () => window.removeEventListener("popstate", onPop);
  });

  function navigate(to: string) {
    history.pushState(null, "", to);
    applyRoute();
  }

  /**
   * Паст создан из /new. Меняем только URL: редактор уже владеет состоянием
   * и своим slug, перезагружать и пересоздавать его не нужно — иначе терялись
   * бы курсор, чат и статус стриминга.
   */
  function onCreated(slug: string) {
    history.replaceState(null, "", `/p/${slug}`);
  }
</script>

{#if !checkedSession}
  <div class="center muted">loading…</div>
{:else if route.kind === "paste"}
  {#if loading}
    <div class="center muted">loading…</div>
  {:else if notFound}
    <div class="center">
      <p class="error">paste not found.</p>
      <button onclick={() => navigate("/new")}>create new</button>
    </div>
  {:else if paste}
    <Editor
      slug={paste.slug}
      initialTitle={paste.title}
      initialFiles={paste.files}
      isOwner={paste.isOwner}
      onCreated={onCreated}
    />
  {/if}
{:else if route.kind === "new"}
  {#if hasSession}
    <Editor slug={null} onCreated={onCreated} />
  {:else}
    <div class="landing">
      <h1>vetchka</h1>
      <p class="muted">для создания пастов нужна сессия</p>
      <SessionGate onReady={() => (hasSession = true)} />
    </div>
  {/if}
{:else}
  <div class="landing">
    <h1>vetchka</h1>
    <p class="muted">минималистичная песочница для кода</p>
    {#if hasSession}
      <button class="primary" onclick={() => navigate("/new")}>start editing</button>
    {:else}
      <SessionGate onReady={() => navigate("/new")} />
    {/if}
  </div>
{/if}

<style>
  .center {
    height: 100%; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 12px;
  }
  .landing {
    height: 100%;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 16px; padding: 24px; text-align: center;
  }
  .landing h1 { margin: 0; font-size: 32px; letter-spacing: -0.02em; }
  .landing p { margin: 0 0 12px; }
</style>